import {
  faBell,
  faCalendar,
  faCancel,
  faCheck,
  faClone,
  faLink,
  faPen,
  faTag,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ButtonV2,
  ButtonV4,
  useAuth,
  ColumnActionWithMenuColors,
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  VerticalDivider,
} from '@labrav/react-components'
import { useStyles } from './ResellerAuthorizationHeader.styles'

import { useCallback, useState, useMemo } from 'react'
import { Typography } from '@mui/material'

import { useDispatch, useSelector } from '../../../../store'
import { useNavigate, useParams } from 'react-router-dom'
import type { ViewResellerAuthorizationType } from '../../../../modules/receivedResellerAuthorization/types'
import { ResellerAgreementStatus } from '../../../../types/flyout'
import type { ResaleAuthorizationsResellerAgreements } from '../../../../types/resaleAuthorization'
import { OfferHeaderIcon } from '../../../OfferHeaderIcon/OfferHeaderIcon'
import {
  cloneResaleAuthorization,
  deactivateResaleAuthorization,
} from '../../../../modules/resaleAuthorization/action'
import { useAccountId } from '@labrav/common'
import { noop } from 'lodash'
import { useFlag, useFlagValue } from '@labrav/flags'
import {
  deleteCurrentResaleAuthorization,
  editFailedResaleAuthorizationMPPO,
  reminderResaleAuthorization,
} from '../../../../modules/resaleAuthorization/utils/detailPageActions'
import { updateResaleAuthorizationForUser } from '../../../PrivateOfferForm/Recipients/utils'
import { cancelResaleAuthSuccess } from '../../../../common/utils/messagesContants'
import { OfferReminderTheBuyer } from '../../../../common/components/OfferReminderTheBuyer/OfferReminderTheBuyer'
import type { CloudMarketplace } from '../../../../types/index'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import {
  LoadingTypes,
  isLoading,
  isLoading as Loading,
  useCurrentCloudProvider,
  copyToClipboard,
} from '@labrav/common'
import {
  DownloadDetailPdfButton,
  DetailPdfCloud,
  DetailPdfPageType,
} from '../../../../common/components/DownloadDetailPdfButton'
import {
  getDeleteResellerDisabledTooltip,
  permissionMessageString,
  RESALE_AUTH_REVAMP_CLONE_ALLOWED_STATUSES,
} from './helpers'

type ResellerAuthorizationHeaderActionsProps = {
  resellerAuthorization: ViewResellerAuthorizationType
  crmOpportunityId?: string
  offerExpiringSoon?: boolean
  externalBaseURL?: string
  search?: string
}

export { getDeleteResellerDisabledTooltip } from './helpers'

export const ResellerAuthorizationHeaderActions = ({
  resellerAuthorization,
  crmOpportunityId,
  offerExpiringSoon,
  externalBaseURL,
  search,
}: ResellerAuthorizationHeaderActionsProps) => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {
    cloud: routeCloud,
    accountId: routeAccountId,
    partnerId: routePartnerId,
  } = useParams<{
    cloud: string
    accountId: string
    partnerId?: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || routePartnerId || fallbackAccountId || ''
  const { productId: productIdParam } = useParams<{
    productId: string
  }>()

  const cloudMarketplace = useCurrentCloudProvider()

  const { loading: authLoading, canDo } = useAuth()
  const canWriteMarketplace = useMemo(() => {
    return (
      !authLoading &&
      canDo({
        cloud: (cloudMarketplace as string)?.toLowerCase(),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, cloudMarketplace])

  const { status, agreementOrigin, offerStatus } = resellerAuthorization

  const isReceivedResellerAuth = agreementOrigin === 'RECEIVED'

  const [openCloneModal, setOpenCloneModal] = useState(false)
  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false)
  const [openReminderTheResellerModal, setOpenReminderTheResellerModal] =
    useState(false)

  const [openEditOfferModal, setOpenEditOfferModal] = useState(false)

  const [copiedOfferLink, setCopiedOfferLink] = useState(false)

  const handleClickEditFailedPO = () => {
    setOpenEditOfferModal(true)
  }
  const handleEditOfferCloseModal = () => {
    setOpenEditOfferModal(false)
  }

  const handleOpenReminderTheResellerModal = () => {
    setOpenReminderTheResellerModal(true)
  }
  const handleOpenCloneModal = () => {
    setOpenCloneModal(true)
  }

  const handleCloseCloneModal = () => {
    setOpenCloneModal(false)
  }

  const handleOpenDeactivateModal = () => {
    setOpenDeactivateModal(true)
  }

  const handleCloseDeactivateModal = () => {
    setOpenDeactivateModal(false)
  }

  const isLoadingSubmit = useSelector(state =>
    Loading(state.loading)(LoadingTypes.UPDATE_FLYOUT_RESELLER_AUTHORIZATION)
  )

  const isLoadingEditOffer = useSelector(state =>
    Loading(state.loading)(LoadingTypes.UPDATE_RESALE_AUTHORIZATION)
  )

  const isVisibleViewChannelOffersOrContracts =
    cloudMarketplace === 'AWS' &&
    [
      ResellerAgreementStatus.ACTIVE,
      ResellerAgreementStatus.DEACATIVATION_IN_PROGRESS,
      ResellerAgreementStatus.RESTRICTED,
      ResellerAgreementStatus.COMPLETED,
      ResellerAgreementStatus.FAILED,
    ].includes(status)

  const handleNext = (id: string, clonedCrmOpportunityId?: string) => {
    const productId =
      cloudMarketplace === 'AWS'
        ? resellerAuthorization?.products?.[0]?.awsProductId ||
          resellerAuthorization?.awsProductId
        : productIdParam

    if (externalBaseURL) {
      const query = new URLSearchParams(search || '')
      query.set('from', 'clone')
      const opportunityId = crmOpportunityId || clonedCrmOpportunityId
      if (opportunityId) {
        query.set('crm_opportunity_id', opportunityId)
      }
      navigate({
        pathname: `${externalBaseURL}/resale-authorizations/${id}/edit`,
        search: `?${query.toString()}`,
      })
    } else if (productId) {
      navigate(
        `/flyout/${routeCloud}/${accountId}/products-listing/${productId}/resale-authorizations/${id}/edit`
      )
    }
  }

  const handleConfirmCloneModal = useCallback(() => {
    const productId =
      cloudMarketplace === 'AWS'
        ? resellerAuthorization?.products?.[0]?.awsProductId ||
          resellerAuthorization?.awsProductId
        : productIdParam
    if (accountId && productId) {
      dispatch(
        cloneResaleAuthorization({
          partnerId: accountId,
          productId,
          resaleAuthorizationState:
            resellerAuthorization as ResaleAuthorizationsResellerAgreements,
          handleNext,
          cloud: cloudMarketplace,
          cloneId: resellerAuthorization?.id,
          crmOpportunityId,
        })
      )
    }
  }, [
    cloudMarketplace,
    resellerAuthorization,
    accountId,
    productIdParam,
    search,
    crmOpportunityId,
  ])

  const handleNextDeactivate = () => {
    handleCloseDeactivateModal()
  }

  const handleConfirmDeactivateModal = () => {
    const productId =
      resellerAuthorization?.products?.[0]?.awsProductId ||
      resellerAuthorization?.awsProductId
    if (accountId && productId) {
      dispatch(
        deactivateResaleAuthorization({
          partnerId: accountId,
          status: ResellerAgreementStatus.DEACATIVATION_IN_PROGRESS,
          handleNext: handleNextDeactivate,
          awsAgreementId: resellerAuthorization?.awsAgreementId,
          resellerAuthorizationId: resellerAuthorization?.id,
        })
      )
    }
  }

  const viewChannelOffers = () => {
    //will add once route created...
  }

  const viewContracts = () => {
    navigate(`/flyout/${routeCloud}/${accountId}/subscriptions`)
  }

  const deleteLoading = useSelector(state =>
    isLoading(state.loading)(LoadingTypes.DELETE_RESALE_AUTHORIZATION)
  )

  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [selectedAgreementForDelete, setSelectedAgreementForDelete] =
    useState<ViewResellerAuthorizationType | null>(null)
  const handleOpenDeleteModal = (agreement: ViewResellerAuthorizationType) => {
    setSelectedAgreementForDelete(agreement)
    setOpenDeleteModal(true)
  }
  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setSelectedAgreementForDelete(null)
  }

  const handleConfirmDeleteModal = async () => {
    if (!selectedAgreementForDelete) return

    await dispatch(
      deleteCurrentResaleAuthorization(
        selectedAgreementForDelete.id,
        undefined,
        accountId,
        cloudMarketplace
      )
    )

    handleCloseDeleteModal()
  }

  const isVisibleMoreMenu =
    (cloudMarketplace === 'AWS' &&
      [ResellerAgreementStatus.ACTIVE].includes(status)) ||
    [ResellerAgreementStatus.FAILED].includes(status)

  const handleSelectOption = (option: ColumnMenuOption) => {
    if (option.key === 'deactivate-reseller-authorization') {
      handleOpenDeactivateModal()
    } else if (option.key === 'delete-offer') {
      handleOpenDeleteModal(resellerAuthorization)
    }
  }

  const handleOpenCancelModal = () => {
    setOpenCancelModal(true)
  }

  const handleCloseCancelModal = () => {
    setOpenCancelModal(false)
  }

  const handleConfirmCancelOffer = () => {
    dispatch(
      updateResaleAuthorizationForUser(
        resellerAuthorization.id,
        {
          state: 'draft',
          operation: 'UPDATE',
        },
        undefined,
        cloudMarketplace,
        false,
        handleCloseCancelModal,
        cancelResaleAuthSuccess,
        undefined,
        accountId
      )
    )
  }

  const isVisibleCancel =
    cloudMarketplace === 'AZURE' &&
    offerStatus &&
    [
      ResellerAgreementStatus.OFFER_SENT_RESELLER,
      ResellerAgreementStatus.OFFER_VIEWED_RESELLER,
    ].includes(offerStatus)

  const isVisibleReminderAction =
    cloudMarketplace === 'AZURE' &&
    offerStatus &&
    [
      ResellerAgreementStatus.OFFER_SENT_RESELLER,
      ResellerAgreementStatus.OFFER_VIEWED_RESELLER,
      ResellerAgreementStatus.OFFER_EXTENDED_TO_BUYER,
      ResellerAgreementStatus.OFFER_TERMS_ACCEPTED_BUYER,
      ResellerAgreementStatus.OFFER_PURCHASE_COMPLETE,
    ].includes(offerStatus)

  const handleSubmitResellerReminder = (data: Record<string, unknown>) => {
    dispatch(
      reminderResaleAuthorization({
        partnerId: accountId,
        data,
        resaleAuthorizationId: resellerAuthorization.id,
        handleSuccess: () => setOpenReminderTheResellerModal(false),
      })
    )
  }

  const handleEditSuccess = () => {
    if (productIdParam) {
      if (externalBaseURL) {
        const query = new URLSearchParams(search || '')
        query.set('from', 'edit')
        navigate({
          pathname: `${externalBaseURL}/resale-authorizations/${resellerAuthorization.id}/edit`,
          search: `?${query.toString()}`,
        })
      } else {
        navigate(
          `/flyout/products-listing/${productIdParam}/resale-authorizations/${resellerAuthorization.id}/edit`
        )
      }
    }
  }

  const handleEditOfferConfirm = () => {
    const crmOpportunityId = new URLSearchParams(search || '')
      .get('hs_object_id')
      ?.toLowerCase()

    dispatch(
      editFailedResaleAuthorizationMPPO({
        resaleAuthorizationId: resellerAuthorization.id,
        handleSuccess: handleEditSuccess,
        partnerId: accountId,
        productId: productIdParam,
        crmOpportunityId,
      })
    )
  }

  const isVisibleOfferLink =
    cloudMarketplace === 'AZURE' &&
    resellerAuthorization?.acceptanceLink &&
    offerStatus &&
    ([
      ResellerAgreementStatus.OFFER_SENT_RESELLER,
      ResellerAgreementStatus.OFFER_VIEWED_RESELLER,
      ResellerAgreementStatus.OFFER_EXTENDED_TO_BUYER,
      ResellerAgreementStatus.OFFER_TERMS_ACCEPTED_BUYER,
      ResellerAgreementStatus.OFFER_PURCHASE_COMPLETE,
      ResellerAgreementStatus.OFFER_EXPIRED,
    ].includes(offerStatus) ||
      offerExpiringSoon)

  const copyOfferLink = async () => {
    if (!resellerAuthorization?.acceptanceLink) {
      console.error('No offer link found')
      return
    }

    try {
      await copyToClipboard(resellerAuthorization?.acceptanceLink)
      setCopiedOfferLink(true)
      setTimeout(() => {
        setCopiedOfferLink(false)
      }, 2000)
    } catch (error) {
      console.error('Error copying URL to clipboard:', error)
    }
  }
  return (
    <div className={classes.root} data-testid={'resale-auth-header-actions'}>
      <div className={classes.leftContainer}>
        {!isReceivedResellerAuth && (
          <>
            <LabraToolTipV2
              tooltipPlacement="bottom"
              description={
                !canWriteMarketplace ? permissionMessageString : undefined
              }
            >
              <ButtonV4
                className={classes.linkItem}
                variant="tertiary"
                testId={'clone-offer'}
                onClick={handleOpenCloneModal}
                disabled={
                  !canWriteMarketplace ||
                  (
                    cloudMarketplace !== 'AZURE' &&
                    !RESALE_AUTH_REVAMP_CLONE_ALLOWED_STATUSES.includes(status))
                }
              >
                <OfferHeaderIcon
                  icon={faClone}
                  style={{
                    width: '16px',
                    height: '16px',
                    padding: '0px 8px 0px 0px',
                  }}
                />
                Clone
              </ButtonV4>
            </LabraToolTipV2>
          </>
        )}
        {isVisibleOfferLink && (
          <>
            <VerticalDivider height="24px" />
            <LabraToolTipV2
              description={`${copiedOfferLink ? 'Copied' : ''}`}
              tooltipPlacement="top"
              icon={
                <FontAwesomeIcon
                  icon={faCheck}
                  style={{ width: '12px', height: '12px' }}
                />
              }
              tooltipPlacmentGap="0"
            >
              <ButtonV4
                className={classes.linkItem}
                variant="tertiary"
                onClick={copyOfferLink}
                disabled={offerStatus === ResellerAgreementStatus.OFFER_EXPIRED}
              >
                <OfferHeaderIcon
                  icon={faLink}
                  style={{
                    width: '20px',
                    height: '16px',
                    padding: '0px 6px 0px 0px',
                  }}
                />
                Offer link
              </ButtonV4>
            </LabraToolTipV2>
          </>
        )}
        {resellerAuthorization?.errorDetails &&
          resellerAuthorization?.errorDetails?.errors &&
          cloudMarketplace === 'AZURE' &&
          offerStatus === ResellerAgreementStatus.OFFER_CREATION_FAILED && (
            <>
              <VerticalDivider height="24px" />
              <ButtonV4
                className={classes.linkItem}
                variant="tertiary"
                data-testid={'failed-offer-edit'}
                onClick={handleClickEditFailedPO}
              >
                <OfferHeaderIcon
                  icon={faPen}
                  style={{
                    width: '16px',
                    height: '16px',
                    padding: '0px 8px 0px 0px',
                  }}
                />
                Edit
              </ButtonV4>
            </>
          )}
        {isVisibleCancel && (
          <>
            <VerticalDivider height="24px" />
            <ButtonV4
              variant="tertiary"
              className={classes.linkItem}
              data-testid={'offer-cancel'}
              onClick={handleOpenCancelModal}
            >
              <OfferHeaderIcon
                icon={faCancel}
                style={{
                  width: '16px',
                  height: '16px',
                  padding: '0px 8px 0px 0px',
                }}
              />
              Cancel
            </ButtonV4>
          </>
        )}
        {isVisibleReminderAction && (
          <>
            <VerticalDivider height="24px" />
            <ButtonV4
              variant="tertiary"
              className={classes.linkItem}
              data-testid={'offer-cancel'}
              onClick={handleOpenReminderTheResellerModal}
            >
              <OfferHeaderIcon icon={faBell} />
              Remind reseller
            </ButtonV4>
          </>
        )}
        {isVisibleViewChannelOffersOrContracts && (
          <>
            {!isReceivedResellerAuth && <VerticalDivider height="24px" />}
            <LabraToolTipV2
              tooltipPlacement="bottom"
              description={'Coming soon'}
            >
              <ButtonV2
                data-testid={'view-channel-offers'}
                styleType="link"
                onClick={viewChannelOffers}
                className={classes.btnDisabled}
              >
                <OfferHeaderIcon icon={faTag} />
                View channel offers
              </ButtonV2>
            </LabraToolTipV2>
            <VerticalDivider height="24px" />
            <LabraToolTipV2
              tooltipPlacement="bottom"
              description={'Coming soon'}
            >
              <ButtonV2
                data-testid={'view-contracts'}
                styleType="link"
                onClick={viewContracts}
                className={classes.btnDisabled}
              >
                <OfferHeaderIcon icon={faCalendar} />
                View contracts
              </ButtonV2>
            </LabraToolTipV2>
          </>
        )}
        <ModalConfirmV2
          title={'Clone reseller authorization?'}
          content={
            <>
              <Typography className={classes.modalText}>
                <p>
                  All data from this reseller authorization will be copied to
                  the new reseller authorization form.
                </p>

                <p>
                  <span className={classes.descriptionNote}>Note:</span> You can
                  edit all information after cloning in the next step.
                </p>
              </Typography>
              <div className={classes.rowRightItem}>
                <ButtonV2
                  data-testid="send-reminder"
                  smallVariant
                  styleType="outlined"
                  onClick={handleCloseCloneModal}
                >
                  Cancel
                </ButtonV2>
                <ButtonV2
                  data-testid="send-reminder"
                  smallVariant
                  styleType="primary"
                  onClick={handleConfirmCloneModal}
                  loading={isLoadingSubmit}
                >
                  Clone reseller authorization
                </ButtonV2>
              </div>
            </>
          }
          open={openCloneModal}
          onDecline={handleCloseCloneModal}
          variant={ModalSizeVariants.small}
          icon={
            <FontAwesomeIcon
              icon={faClone}
              style={{ width: '14px', height: '14px' }}
            />
          }
          hideCancelIcon={false}
          hideCancelButton={true}
          hideAcceptButton={true}
          onAccept={noop}
        />
      </div>
      {(isVisibleMoreMenu || cloudMarketplace === 'AWS') && (
        <div className={classes.leftContainer}>
          {cloudMarketplace === 'AWS' && resellerAuthorization?.id && (
            <DownloadDetailPdfButton
              pageType={DetailPdfPageType.RESELLER_AUTH}
              partnerId={accountId}
              entityId={resellerAuthorization.id}
              cloud={DetailPdfCloud.AWS}
              testId="download-reseller-auth-pdf"
            />
          )}
          {cloudMarketplace === 'AWS' && isVisibleMoreMenu && (
            <VerticalDivider height="24px" />
          )}
          {isVisibleMoreMenu && (
            <ColumnActionWithMenuV2
              iconColor={ColumnActionWithMenuColors.BLUE}
              columnTitle={<div className={classes.moreMenu}>More</div>}
              menuOptions={[
                {
                  key: 'delete-offer',
                  label: 'Delete authorization',
                  disable: status !== ResellerAgreementStatus.FAILED,
                  disabledTooltip: getDeleteResellerDisabledTooltip(
                    resellerAuthorization as any,
                    cloudMarketplace
                  ),
                },
                {
                  key: 'deactivate-reseller-authorization',
                  label: 'Deactivate',
                  disable: status !== ResellerAgreementStatus.ACTIVE,
                },
              ]}
              handleSelectOption={handleSelectOption}
            />
          )}
        </div>
      )}
      <ModalConfirmV2
        title={'Delete this authorization? '}
        description={
          <Typography>
            <p>
              You will not be able to recover this authorization or any
              information inside it once this authorization is deleted.
            </p>

            <p>Are you sure you want to delete this Reseller authorization?</p>
          </Typography>
        }
        open={openDeleteModal}
        titleAcceptButton={'Delete'}
        onAccept={handleConfirmDeleteModal}
        acceptLoading={deleteLoading}
        onDecline={handleCloseDeleteModal}
        variant={ModalSizeVariants.small}
        icon={
          <FontAwesomeIcon
            icon={faTrash}
            style={{ width: '14px', height: '16px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={false}
      />

      <ModalConfirmV2
        title={'Deactivate this authorization?'}
        content={
          <>
            <Typography className={classes.modalText}>
              <p>
                Your reseller authorization will be deactivated and your channel
                partner will no longer be able to use it for channel offer
                creation.
              </p>

              <p>Are you sure you want to deactivate?</p>
            </Typography>
            <div className={classes.rowRightItem}>
              <ButtonV2
                data-testid="cancel-deactivate"
                smallVariant
                styleType="outlined"
                onClick={handleCloseDeactivateModal}
              >
                Cancel
              </ButtonV2>
              <ButtonV2
                data-testid="send-deactivate"
                smallVariant
                styleType="primary"
                onClick={handleConfirmDeactivateModal}
                loading={isLoadingSubmit}
              >
                Deactivate
              </ButtonV2>
            </div>
          </>
        }
        open={openDeactivateModal}
        onDecline={handleCloseDeactivateModal}
        variant={ModalSizeVariants.small}
        icon={
          <FontAwesomeIcon
            icon={faCancel}
            style={{ width: '14px', height: '14px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={true}
        hideAcceptButton={true}
        onAccept={noop}
      />
      <ModalConfirmV2
        title={'Cancel this authorization?'}
        content={
          <>
            <Typography className={classes.modalText}>
              <p>
                Your offer will be cancelled on the Microsoft Marketplace, and
                you will receive an email notification.
                <p>
                  Please note that once cancelled, this offer cannot be
                  restored.
                </p>
              </p>

              <p>Are you sure you want to cancel this offer?</p>
            </Typography>
            <div className={classes.rowRightItem}>
              <ButtonV2
                data-testid="cancel-deactivate"
                smallVariant
                styleType="outlined"
                onClick={handleCloseCancelModal}
              >
                Close
              </ButtonV2>
              <ButtonV2
                data-testid="send-deactivate"
                smallVariant
                styleType="primary"
                onClick={handleConfirmCancelOffer}
                loading={isLoadingSubmit}
              >
                Confirm
              </ButtonV2>
            </div>
          </>
        }
        open={openCancelModal}
        onDecline={handleCloseCancelModal}
        variant={ModalSizeVariants.small}
        icon={
          <FontAwesomeIcon
            icon={faCancel}
            style={{ width: '14px', height: '14px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={true}
        hideAcceptButton={true}
        onAccept={noop}
      />
      <ModalConfirmV2
        icon={
          <FontAwesomeIcon
            icon={faPen}
            style={{ width: '14px', height: '14px' }}
          />
        }
        title={'Edit reseller authorization?'}
        description={
          <Typography className={classes.modalText}>
            <p data-testid={'edit-offer-modal'}>
              Clicking this will change the reseller authorization to{' '}
              <span className={classes.draftState}>draft </span>
              state.
              <br />
              Are you sure you want to edit this reseller authorization ?
            </p>
          </Typography>
        }
        open={openEditOfferModal}
        titleAcceptButton={'Edit'}
        titleCancelButton={'Cancel'}
        onAccept={handleEditOfferConfirm}
        onDecline={handleEditOfferCloseModal}
        variant={ModalSizeVariants.small}
        acceptLoading={isLoadingEditOffer}
      />
      <OfferReminderTheBuyer
        cloudMarketplace={routeCloud as CloudMarketplace}
        partnerId={accountId || ''}
        offerId={resellerAuthorization?.id}
        offerRecipients={resellerAuthorization?.offerRecipients || []}
        openReminderTheBuyerModal={openReminderTheResellerModal}
        setOpenReminderTheBuyerModal={setOpenReminderTheResellerModal}
        handleSubmit={handleSubmitResellerReminder}
        title="Remind reseller"
        isSubmitLoading={isLoadingSubmit}
      />
    </div>
  )
}
